import { useEffect, useState } from 'react';

/**
 * Minimal typings for the Places API (New) Autocomplete element we use.
 * Avoids pulling in the full @types/google.maps dependency.
 *
 * Migrated from the legacy `google.maps.places.Autocomplete` widget (which
 * attached to an existing <input>) to `PlaceAutocompleteElement` — a custom
 * element that renders its own internal input, since the legacy widget's
 * backing API can't be enabled on this Google Cloud project. See
 * https://developers.google.com/maps/documentation/javascript/place-autocomplete-new
 */
declare global {
  interface Window {
    google?: {
      maps: {
        places: {
          PlaceAutocompleteElement: new (opts?: {
            includedRegionCodes?: Array<string>;
          }) => GooglePlaceAutocompleteElement;
        };
      };
    };
  }
}

export type GooglePlace = {
  formattedAddress?: string | null;
  fetchFields: (opts: { fields: Array<string> }) => Promise<void>;
};

export type GooglePlacePrediction = {
  toPlace: () => GooglePlace;
};

export type GooglePlaceAutocompleteElement = HTMLElement & {
  includedRegionCodes?: Array<string>;
  addEventListener(
    type: 'gmp-select',
    listener: (event: { placePrediction: GooglePlacePrediction }) => void,
  ): void;
  removeEventListener(type: 'gmp-select', listener: (event: { placePrediction: GooglePlacePrediction }) => void): void;
};

type LoadState = 'idle' | 'loading' | 'ready' | 'error' | 'no-key';

const SCRIPT_ID = 'google-maps-places';

/**
 * Loads the Google Maps JS API (places library) on the client using
 * `APP_GOOGLE_MAPS_API_KEY` (served to the browser via `window.ENV`, see
 * `getPublicEnv` in config.server.ts). Returns the current load state. When no
 * key is configured, returns `no-key` so callers can degrade gracefully.
 */
export function useGoogleMaps(): LoadState {
  const [state, setState] = useState<LoadState>('idle');

  useEffect(() => {
    const apiKey = window.ENV?.GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      setState('no-key');
      return;
    }

    if (window.google?.maps?.places) {
      setState('ready');
      return;
    }

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;

    const onReady = () => setState('ready');
    const onError = () => setState('error');

    if (existing) {
      setState('loading');
      existing.addEventListener('load', onReady);
      existing.addEventListener('error', onError);
      return () => {
        existing.removeEventListener('load', onReady);
        existing.removeEventListener('error', onError);
      };
    }

    setState('loading');
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=en&region=AU`;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', onReady);
    script.addEventListener('error', onError);
    document.head.appendChild(script);

    return () => {
      script.removeEventListener('load', onReady);
      script.removeEventListener('error', onError);
    };
  }, []);

  return state;
}
